import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Card,
} from 'antd';
import {
  PlayCircleFilled,
  FileTextOutlined,
  BarChartOutlined,
  MailOutlined,
  SearchOutlined,
  DiffOutlined,
} from '@ant-design/icons';
import { Workspace, CommonAction } from '../../types';

interface CommonActionsProps {
  workspace: Workspace;
  onActionClick: (prompt: string, title: string) => void;
}

interface ActionDisplay {
  id: string;
  title: string;
  prompt: string;
  icon: React.ReactNode;
  category: string;
}

export const CommonActions: React.FC<CommonActionsProps> = ({ workspace, onActionClick }) => {
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const [workspaceWithActions, setWorkspaceWithActions] = useState<Workspace | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{x: number, y: number} | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const iconRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  
  // Force fetch full workspace details if we don't have common actions
  useEffect(() => {
    const fetchFullWorkspace = async () => {
      if (workspace && !workspace.commonActions && !workspaceWithActions) {
        try {
          const { apiService } = await import('../../services/api');
          const fullWorkspace = await apiService.getWorkspace(workspace.id);
          setWorkspaceWithActions(fullWorkspace);
        } catch (error) {
          console.error('Failed to fetch full workspace details:', error);
        }
      } else if (workspace?.commonActions && !workspaceWithActions) {
        setWorkspaceWithActions(workspace);
      }
    };

    fetchFullWorkspace();
  }, [workspace?.id, workspace?.commonActions, workspaceWithActions]);
  
  // Helper function to get icon from icon name (supports both Ant Design and Font Awesome Pro+)
  const getIconFromName = (iconName: string): React.ReactNode => {
    const iconStyle = { fontSize: '16px' };
    
    // Check if it's a Font Awesome icon (starts with fa-)
    if (iconName.startsWith('fa-')) {
      let iconClass = 'fas'; // Default to solid
      let actualIconName = iconName;
      
      // Handle Font Awesome Pro+ style variants
      if (iconName.startsWith('fa-regular-')) {
        iconClass = 'far';
        actualIconName = iconName.replace('fa-regular-', 'fa-');
      } else if (iconName.startsWith('fa-light-')) {
        iconClass = 'fal';
        actualIconName = iconName.replace('fa-light-', 'fa-');
      } else if (iconName.startsWith('fa-duotone-')) {
        iconClass = 'fad';
        actualIconName = iconName.replace('fa-duotone-', 'fa-');
      } else if (iconName.startsWith('fa-brands-')) {
        iconClass = 'fab';
        actualIconName = iconName.replace('fa-brands-', 'fa-');
      }
      
      return (
        <i 
          className={`${iconClass} ${actualIconName}`} 
          style={iconStyle}
        />
      );
    }
    
    // Ant Design icons
    switch (iconName) {
      case 'BarChartOutlined':
        return <BarChartOutlined style={iconStyle} />;
      case 'SearchOutlined':
        return <SearchOutlined style={iconStyle} />;
      case 'PlayCircleFilled':
        return <PlayCircleFilled style={iconStyle} />;
      case 'FileTextOutlined':
        return <FileTextOutlined style={iconStyle} />;
      case 'MailOutlined':
        return <MailOutlined style={iconStyle} />;
      case 'DiffOutlined':
        return <DiffOutlined style={iconStyle} />;
      default:
        return <PlayCircleFilled style={iconStyle} />;
    }
  };

  // Convert database common actions to display format
  const convertToDisplayActions = (dbActions: CommonAction[]): ActionDisplay[] => {
    return dbActions.map(action => ({
      id: action.id,
      title: action.name,
      prompt: action.command,
      icon: getIconFromName(action.icon),
      category: action.category
    }));
  };

  // Get actions from database or fallback to empty array
  const currentWorkspace = workspaceWithActions || workspace;
  const actions = currentWorkspace?.commonActions ? convertToDisplayActions(currentWorkspace.commonActions) : [];

  const handleActionClick = (action: ActionDisplay) => {
    onActionClick(action.prompt, action.title);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (actionId: string) => {
    const iconElement = iconRefs.current[actionId];
    if (iconElement) {
      const rect = iconElement.getBoundingClientRect();
      setPopoverPosition({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 5,
      });
    }
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Set 1 second delay
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredAction(actionId);
    }, 1000);
  };

  const handleMouseLeave = () => {
    // Clear timeout and hide popover
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredAction(null);
    setPopoverPosition(null);
  };

  return (
  <>
    <Card style={{ 
      marginBottom: '1rem', 
      border: 'none', 
      boxShadow: 'none',
      backgroundColor: 'var(--color-bg-primary)',
      color: 'var(--color-text-primary)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <PlayCircleFilled style={{ fontSize: '20px' }} />
        <h4 style={{ margin: 0, fontWeight: 'bold' }}>COMMON ACTIONS</h4>
      </div>
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.25rem' 
      }}>
        {actions.length === 0 ? (
          <div style={{ 
            color: 'var(--color-text-secondary)', 
            fontSize: 'var(--font-size-sm)', 
            padding: '0.5rem 0.75rem',
            textAlign: 'center'
          }}>
            No common actions available for this workspace
          </div>
        ) : (
          actions.map((action) => (
          <div key={action.id}>
            <div
              ref={(el) => { iconRefs.current[action.id] = el; }}
              onClick={() => handleActionClick(action)}
              onMouseEnter={() => handleMouseEnter(action.id)}
              onMouseLeave={handleMouseLeave}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick(action);
                }
              }}
              style={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                padding: '0.5rem 0.75rem',
                paddingLeft: '1.75rem', // Reduced by ~0.5rem (one character width) for better alignment
                height: 'auto',
                minHeight: '36px',
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.11s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ 
                flexShrink: 0, 
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text-primary)',
                marginTop: '0.1rem'
              }}>
                {action.icon}
              </span>
              <span style={{ 
                fontSize: 'var(--font-size-md)',
                color: 'var(--color-text-primary)',
                flex: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 'var(--line-height-tight)',
                alignSelf: 'flex-start'
              }}>
                {action.title}
              </span>
            </div>
          </div>
        )))}
      </div>
    </Card>
    
    {/* Portal-based popover that renders outside the container - matches Data Sources styling */}
    {hoveredAction && popoverPosition && (
      createPortal(
        <div
          style={{
            position: 'fixed',
            top: `${popoverPosition.y}px`,
            left: `${popoverPosition.x}px`,
            transform: 'translateX(-50%)',
            zIndex: 99999,
            backgroundColor: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '6px',
            padding: '0.75rem',
            width: '320px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            lineHeight: '1.5',
            textAlign: 'left',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            minHeight: 'auto',
            pointerEvents: 'none',
          }}
        >
          {(() => {
            const action = actions.find(a => a.id === hoveredAction);
            return action ? (
              <>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '0.5rem',
                  color: 'var(--color-text-primary)'
                }}>
                  {action.title}
                </div>
                {action.prompt}
              </>
            ) : null;
          })()}
        </div>,
        document.body
      )
    )}
  </>);
};